import type { Estimation } from "@/interfaces/estimation.interface";
import { isValidUrl } from "@/util/validator";
import { Plus, Trash2, Link as LinkIcon } from "lucide-react";
import { useCallback, useState } from "react";
import { InputField } from "../../input/InputField";
import { Button } from "@/components/ui/button";
import { EmptyTabState } from "./EmptyTabState";

interface LinksProps extends Pick<Estimation, "links"> {
  hideHeader?: boolean;
  updateLinks: (links: string[]) => Promise<boolean>;
}

export const Links = ({ links, hideHeader, updateLinks }: LinksProps) => {
  const [link, setLink] = useState<string>();
  const [error, setError] = useState<string>();
  const [showAddInput, setShowAddInput] = useState(false);

  const toggleShowAddInput = useCallback(() => {
    setShowAddInput((prev) => !prev);
  }, []);

  const onAddBlur = useCallback(
    async (e: React.FocusEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const isValid = isValidUrl(value);

      if (!isValid) {
        setError("Invalid url");
        return;
      }

      const updated = await updateLinks([...links, value]);

      if (!updated) {
        setError("Unable to save link");
        return;
      }

      setLink("");
      setError(undefined);
      toggleShowAddInput();
    },
    [links, toggleShowAddInput, updateLinks],
  );

  const onDelete = useCallback(() => {
    setError(undefined);
    toggleShowAddInput();
  }, [toggleShowAddInput]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setLink(e.target.value);
  }, []);

  if (links.length === 0 && !showAddInput) {
    return (
      <EmptyTabState
        icon={<LinkIcon className="h-6 w-6 text-muted-foreground" />}
        title="No links"
        description="Add links to external resources for this estimate."
        action={
          <Button variant="secondary" size="sm" className="mt-4" onClick={toggleShowAddInput}>
            <Plus className="h-4 w-4" />
            Add link
          </Button>
        }
      />
    );
  }

  return (
    <div>
      {!hideHeader && !showAddInput && (
        <div className="flex justify-end px-4 py-2">
          <Button variant="secondary" size="sm" onClick={toggleShowAddInput}>
            <Plus className="h-4 w-4" />
            Add link
          </Button>
        </div>
      )}

      <div
        className={`flex w-full flex-row transition-all duration-300 ${showAddInput ? "my-4 !h-auto space-x-4 px-4 opacity-100" : "m-0 !h-0 p-0 opacity-0"}`}
      >
        <InputField
          className="w-full"
          error={error}
          name={"add-link"}
          placeHolder="Add link"
          onBlur={onAddBlur}
          value={link}
          onChange={onChange}
        />
        <Trash2 className="mt-[6px] h-6 w-6 cursor-pointer" color="#E45F58" onClick={onDelete} />
      </div>

      <div className="flex flex-col gap-[18px] px-4 py-2">
        {links.map((link) => (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            key={`link-${link}`}
            className="relative z-10 cursor-pointer py-1 text-sm font-medium break-all text-blue-500 select-text hover:!underline"
            style={{ pointerEvents: "auto" }}
            onClick={(e) => e.stopPropagation()}
          >
            {link}
          </a>
        ))}
      </div>
    </div>
  );
};
